import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { Card, CardHeader, CardContent, CardFooter } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { useAuth } from "contexts/AuthContext";
import api from "lib/api";
import brand from "lib/brand";
import { useI18n } from "i18n";

// Ícone de cada provedor SSO (identificado por `key` vindo de /api/auth/config/).
const Office365Icon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
    <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
    <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
    <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
  </svg>
);

const SecFaceIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 500 500" aria-hidden="true">
    <g transform="translate(0,500) scale(0.1,-0.1)" fill="#ee3135" stroke="none">
      <path d="M1395 4372 l-1090 -617 0 -544 0 -543 935 -554 c514 -305 1008 -598 1097 -650 l163 -96 280 178 c181 115 276 181 268 186 -7 4 -503 299 -1103 656 l-1090 649 -3 209 -2 210 806 459 c443 253 815 464 827 469 19 7 199 -94 1087 -609 586 -339 1080 -626 1098 -637 l32 -20 0 329 0 328 -1094 608 c-601 334 -1099 607 -1107 607 -8 0 -504 -278 -1104 -618z" />
      <path d="M2217 3453 c-179 -115 -273 -180 -265 -185 7 -4 504 -299 1103 -656 l1090 -649 3 -209 2 -210 -806 -459 c-443 -253 -815 -464 -827 -469 -19 -7 -199 94 -1087 609 -586 339 -1080 626 -1097 637 l-33 20 0 -329 0 -328 1094 -608 c601 -334 1099 -607 1107 -607 8 0 504 278 1104 618 l1090 617 0 544 0 543 -1095 649 c-602 357 -1097 649 -1100 649 -3 0 -130 -80 -283 -177z" />
    </g>
  </svg>
);

const SSO_ICONS = {
  office365: Office365Icon,
  secface: SecFaceIcon,
};

const SSO_LABEL_KEY = {
  office365: "login.office365",
  secface: "login.secface",
};

function EmailStep({ email, setEmail, onSubmit, loading, error, ssoProviders = [],
                    configError = false, configLoading = false, onRetryConfig }) {
  const { t } = useI18n();
  return (
    <div className="animate-fade-in">
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          {t("login.headline")}
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          {t("login.subtitle")}
        </p>
      </CardHeader>
      <CardContent>
        {/* O visitante nao precisa de conta para nada: quem chega aqui por
            engano tem o caminho de volta para a area publica, onde encontra e
            baixa as proprias fotos sem login. */}
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
          <p className="text-sm font-medium text-amber-800">{t("login.adminOnlyTitle")}</p>
          <p className="text-xs text-amber-700 mt-1">{t("login.adminOnlyHint")}</p>
          <Link
            to="/"
            className="mt-2 inline-block text-xs font-medium text-amber-800 underline hover:no-underline"
          >
            {t("login.goToPublic")}
          </Link>
        </div>
        {configError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-center">
            <p className="text-sm font-medium text-red-600">{t("login.configErrorTitle")}</p>
            <p className="text-xs text-red-500 mt-1">{t("login.configErrorHint")}</p>
            <button type="button" onClick={onRetryConfig}
              className="mt-2 text-xs font-medium text-red-700 underline hover:no-underline">
              {t("login.configRetry")}
            </button>
          </div>
        )}
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">{t("login.email")}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t("login.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!error}
              required
              autoFocus
              autoComplete="email"
              disabled={configError || configLoading}
              className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {error && (
              <p className="text-xs text-red-500 mt-1">{error}</p>
            )}
          </div>
          <Button type="submit" className="w-full" size="lg" loading={loading}
            disabled={configError || configLoading}>
            {t("login.continue")}
          </Button>
        </form>
        {ssoProviders.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 my-3">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-xs text-slate-400 uppercase">{t("login.or")}</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>
            {ssoProviders.map((provider) => {
              const Icon = SSO_ICONS[provider.key];
              const labelKey = SSO_LABEL_KEY[provider.key] || "login.sso";
              return (
                <a
                  key={provider.key}
                  href={provider.login_url}
                  className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-white border border-slate-300 px-3.5 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors duration-150"
                >
                  {Icon && <Icon />}
                  {t(labelKey)}
                </a>
              );
            })}
          </div>
        )}
      </CardContent>
      <CardFooter className="justify-center">
        <p className="text-xs text-center text-gray-500 leading-relaxed max-w-[280px]">
          {t("login.contactHint")}{" "}
          <a
            href={`mailto:${brand.contactEmail}`}
            className="text-blue-600 hover:underline font-medium"
          >
            {brand.contactEmail}
          </a>
        </p>
      </CardFooter>
    </div>
  );
}

function MfaStep({ code, setCode, onSubmit, onBack, loading, error }) {
  const { t } = useI18n();
  const inputRefs = useRef([]);
  const autoSubmitRef = useRef(false);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    if (code.every((d) => d !== "") && !autoSubmitRef.current && !loading) {
      autoSubmitRef.current = true;
      onSubmit(code.join(""));
    }
    if (code.some((d) => d === "")) {
      autoSubmitRef.current = false;
    }
  }, [code, onSubmit, loading]);

  const handleChange = useCallback(
    (index, value) => {
      if (!/^\d*$/.test(value)) return;
      const newCode = [...code];
      newCode[index] = value.slice(-1);
      setCode(newCode);
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [code, setCode]
  );

  const handleKeyDown = useCallback(
    (index, e) => {
      if (e.key === "Backspace" && !code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [code]
  );

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
      if (pasted.length > 0) {
        const newCode = [...code];
        for (let i = 0; i < pasted.length && i < 6; i++) {
          newCode[i] = pasted[i];
        }
        setCode(newCode);
        const focusIndex = Math.min(pasted.length, 5);
        inputRefs.current[focusIndex]?.focus();
      }
    },
    [code, setCode]
  );

  return (
    <div className="animate-fade-in">
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          {t("login.headline")}
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          {t("login.mfaSubtitle")}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-center gap-3" onPaste={handlePaste}>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={code[index]}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`w-12 h-14 text-center text-xl font-semibold text-gray-900 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    error
                      ? "border-red-400 bg-red-50"
                      : code[index]
                      ? "border-blue-400 bg-white"
                      : "border-gray-200 bg-gray-100"
                  }`}
                  autoComplete="one-time-code"
                />
              ))}
            </div>
            {error && (
              <p className="text-xs text-red-500 text-center mt-2">{error}</p>
            )}
          </div>
          <Button type="submit" className="w-full" size="lg" loading={loading}>
            {t("login.validateCode")}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex-col items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          ← {t("common.back")}
        </button>
        <p className="text-xs text-gray-500">
          {t("login.codeNotReceived")}{" "}
          <button
            type="button"
            className="text-blue-600 hover:underline font-medium"
            onClick={onBack}
          >
            {t("login.resend")}
          </button>
        </p>
      </CardFooter>
    </div>
  );
}

function PasswordStep({ password, setPassword, onSubmit, onBack, loading, error }) {
  const { t } = useI18n();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="animate-fade-in">
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          {t("login.headline")}
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          {t("login.passwordSubtitle")}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-700">{t("login.password")}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder={t("login.passwordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!error}
                required
                autoFocus
                autoComplete="current-password"
                className="pr-10 bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <Button type="submit" className="w-full" size="lg" loading={loading}>
            {t("login.signIn")}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          ← {t("common.back")}
        </button>
      </CardFooter>
    </div>
  );
}

function ChangePasswordStep({ tempToken, onSuccess }) {
  const { t } = useI18n();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  // Inline password strength checks
  const checks = [
    { label: t("login.check.minLength"), ok: newPassword.length >= 8 },
    { label: t("login.check.uppercase"), ok: /[A-Z]/.test(newPassword) },
    { label: t("login.check.lowercase"), ok: /[a-z]/.test(newPassword) },
    { label: t("login.check.special"), ok: /[^a-zA-Z0-9]/.test(newPassword) },
    { label: t("login.check.match"), ok: newPassword && newPassword === confirmPassword },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    if (newPassword !== confirmPassword) {
      setErrors([t("login.passwordMismatch")]);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post(
        "/api/auth/change-password/",
        { new_password: newPassword, confirm_password: confirmPassword },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      onSuccess(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setErrors(detail);
      } else if (typeof detail === "string") {
        setErrors([detail]);
      } else {
        setErrors([t("login.changePasswordError")]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          {t("login.changePassword")}
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          {t("login.changePasswordSubtitle")}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-gray-700">{t("login.newPassword")}</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNew ? "text" : "password"}
                placeholder={t("login.newPasswordPlaceholder")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
                autoComplete="new-password"
                className="pr-10 bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-gray-700">{t("login.confirmPassword")}</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirm ? "text" : "password"}
                placeholder={t("login.confirmPasswordPlaceholder")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="pr-10 bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Password strength indicators */}
          {newPassword && (
            <div className="space-y-1.5 pt-1">
              {checks.map((check, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {check.ok ? (
                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <X className="h-3.5 w-3.5 text-gray-300" />
                  )}
                  <span className={check.ok ? "text-emerald-500" : "text-gray-400"}>
                    {check.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {errors.length > 0 && (
            <div className="space-y-1 pt-1">
              {errors.map((err, i) => (
                <p key={i} className="text-xs text-red-500">{err}</p>
              ))}
            </div>
          )}

          <Button type="submit" className="w-full" size="lg" loading={loading}>
            {t("login.changePassword")}
          </Button>
        </form>
      </CardContent>
    </div>
  );
}

export default function Login() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, loginUser } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [mfaCode, setMfaCode] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // For change password flow
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [ssoProviders, setSsoProviders] = useState([]);
  const [configError, setConfigError] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    if (user) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);

  // Carrega a config de autenticação. Se falhar (ex.: backend fora do ar),
  // bloqueia o formulário e avisa — em vez de deixar o usuário tentar logar
  // num serviço indisponível. Tenta algumas vezes (502 transitório de deploy).
  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await api.get("/api/auth/config/");
        setSsoProviders(res.data?.sso_providers || []);
        setConfigError(false);
        setConfigLoading(false);
        return;
      } catch (err) {
        if (attempt < 2) await new Promise((r) => setTimeout(r, 1500));
      }
    }
    setConfigError(true);
    setConfigLoading(false);
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await api.post("/api/auth/email/", { email });
      if (res.data?.skip_mfa) {
        setLoading(false);
        setStep(3);
        return;
      }
    } catch (err) {
      // Always proceed to step 2 regardless of response
    } finally {
      setLoading(false);
    }
    if (step === 1) setStep(2);
  };

  const handleMfaSubmit = async (codeOrEvent) => {
    let codeString;
    if (typeof codeOrEvent === "string") {
      codeString = codeOrEvent;
    } else {
      codeOrEvent.preventDefault();
      codeString = mfaCode.join("");
    }

    if (codeString.length !== 6) {
      setError(t("login.incompleteCode"));
      return;
    }

    setError("");
    setLoading(true);

    try {
      await api.post("/api/auth/mfa/", { code: codeString });
      setStep(3);
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        t("login.invalidCode");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await api.post("/api/auth/password/", { password });

      if (response.data?.must_change_password) {
        // User must change password — show change password form
        setTempToken(response.data.token);
        setMustChangePassword(true);
        setLoading(false);
        return;
      }

      if (response.data?.user && response.data?.token) {
        loginUser(response.data.user, response.data.token);
      }
      navigate("/app/dashboard");
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        t("login.wrongPassword");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChanged = (data) => {
    if (data?.user && data?.token) {
      loginUser(data.user, data.token);
    }
    navigate("/app/dashboard");
  };

  const goToStep = (targetStep) => {
    setError("");
    if (targetStep < step) {
      if (targetStep <= 2) setPassword("");
      if (targetStep <= 1) setMfaCode(["", "", "", "", "", ""]);
    }
    setStep(targetStep);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7] px-4 py-8">
      <div className="w-full max-w-[420px]">
        <Card className="shadow-lg border-0 bg-white rounded-2xl">
          {mustChangePassword ? (
            <ChangePasswordStep
              tempToken={tempToken}
              onSuccess={handlePasswordChanged}
            />
          ) : (
            <>
              {step === 1 && (
                <EmailStep
                  email={email}
                  setEmail={setEmail}
                  onSubmit={handleEmailSubmit}
                  loading={loading}
                  error={error}
                  ssoProviders={ssoProviders}
                  configError={configError}
                  configLoading={configLoading}
                  onRetryConfig={loadConfig}
                />
              )}
              {step === 2 && (
                <MfaStep
                  code={mfaCode}
                  setCode={setMfaCode}
                  onSubmit={handleMfaSubmit}
                  onBack={() => goToStep(1)}
                  loading={loading}
                  error={error}
                />
              )}
              {step === 3 && (
                <PasswordStep
                  password={password}
                  setPassword={setPassword}
                  onSubmit={handlePasswordSubmit}
                  onBack={() => goToStep(2)}
                  loading={loading}
                  error={error}
                />
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
